/**
 * The transcript export (R20/D86) — a played session as a printed artifact,
 * serialized to Calder's target-format contract
 * (`docs/operations/2026-08-26-calder-export-target-format.md`).
 *
 * @remarks
 * The serializer reads the transcript and NOTHING else — endings, asks, and
 * every beat already live in the record, so an export derived from it cannot
 * disagree with what the player saw. The five channel signatures, in the one
 * medium with no CSS: the player's turns are the only glyph-led lines
 * (`▷ <words>`; a blank input is a bare `▷` — literally what happened);
 * documents indent four spaces (a set-apart quoted document, colliding with
 * neither her quotation marks nor the sigil's column); system output takes
 * the SHOUT the stylesheet gives it on screen — except the map, which is a
 * drawing and keeps its casing and newlines verbatim, the stylesheet's own
 * exemption on the same `triggerContext` key; a quoted speaker's lines carry
 * their authored (audit-enforced) quotation marks (D192: the pack declares
 * who is quoted; the POC's narrator is); environment stays unmarked, because
 * absence is the signal. A speaker's line carries a NAME LABEL — `The Tide:
 * the water comes back` — when the speaker is UNQUOTED, or when the pack
 * declares more than one speaker (Mike's ruling, 2026-09-17, on Calder's
 * `who-said-that` note): an unquoted voice without a label prints
 * indistinguishably from the world, and two quoted voices print
 * indistinguishably from each other. D232: an ACTOR's voice is always
 * labeled and never counts toward that tally — the label is the body's
 * signature (Calder's `actors-content.md` §6), and a narrator beside one
 * person in a room stays bare. A colon, never a dash (`WORD — value`
 * is the chrome's shape, and chrome is the shout), and the pack's authored
 * casing, never uppercased — the shout is `system`'s alone. A pack with one
 * quoted speaker exports exactly as it always did. The which-one ask composes its
 * `offeredAnswers` into the question line, closing "a question with no
 * options and an answer to nothing" — and nothing annotates which candidate
 * an answer selected, because the record never explains itself.
 *
 * Determinism is a contract: the same `(bundle, seed, commands)` serializes
 * byte-identically — no date line, UTF-8, `\n`, one trailing newline. The
 * colophon carries the seed because it is the one thing a reader could not
 * reconstruct by reading.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { offeredAnswersSentence } from '../defaults/default-responses.js';
import { exportLabelsBySpeakerOf } from '../narrative/speaker-presentation.js';
import { groupTranscriptEntries } from '../transcript/group-transcript-entries.js';

/** The document-channel indent: the oldest convention for a set-apart quoted document. */
const DOCUMENT_INDENT = '    ';

/**
 * Serialize one session to the export's plain-text artifact.
 *
 * @param bundle - The loaded bundle (the title is the pack's own — the second pack exports as itself)
 * @param state - The session to export, exactly as committed
 * @returns The artifact: title, the session turn by turn, and the colophon
 */
export const serializeTranscript = (bundle: EngineGameBundle, state: RuntimeState): string => {
	const paragraphs: string[] = [bundle.contentPack.manifest.title];
	// Who gets a label: every unquoted voice, everyone once there is more
	// than one disembodied voice to tell apart, and (D232) every actor's
	// voice always. One quoted voice in the ear has her marks and nothing else.
	const labelsBySpeaker = exportLabelsBySpeakerOf(bundle.contentPack);

	for (const group of groupTranscriptEntries(state.session.transcript)) {
		// The boot block has no prompt line — nothing was submitted. Every
		// other group opens with the sigil: the typed words, or the bare `▷`
		// of an empty directive (a hidden heading and an absent heading are
		// the same thing on screen and different things in a record).
		if (group.key !== 'session-start') {
			paragraphs.push(group.headingEntry === undefined ? '▷' : `▷ ${group.headingEntry.text}`);
		}

		for (const entry of group.entries) {
			paragraphs.push(renderEntry(bundle, entry, labelsBySpeaker));
		}
	}

	const colophon = [
		bundle.contentPack.manifest.title,
		`seed ${String(state.randomness.seed)}`,
		`${String(state.progression.turnCount)} turns`,
		`${String(state.session.transcript.length)} entries`,
	].join(' · ');

	return `${[...paragraphs, '———', colophon].join('\n\n')}\n`;
};

/**
 * Render one entry as its paragraph, signature and all.
 *
 * @param bundle - The loaded bundle (D226: which offered names refuse the article)
 * @param entry - The committed entry
 * @param labelsBySpeaker - The names, by id, of the speakers whose lines carry a label
 * @returns The paragraph, exactly as the artifact prints it
 */
const renderEntry = (bundle: EngineGameBundle, entry: RuntimeTranscriptEntry, labelsBySpeaker: ReadonlyMap<string, string>): string => {
	// The map is a DRAWING: authored casing and internal newlines verbatim,
	// no indent — the stylesheet's exemption, honored on the same key.
	if (entry.triggerContext === 'map') {
		return entry.text;
	}

	// The which-one ask carries what it offered into the record (D86). Since
	// D229 (E14) the line itself names the options; a line that does not — a
	// record from before, or a pack whose re-voiced ask left `{options}` out
	// — has them composed in here, so no record shows a question with no
	// options.
	const spokenText = namingItsOptions(bundle, entry);

	switch (entry.source) {
		case 'human_log': {
			return spokenText
				.split('\n')
				.map((line) => `${DOCUMENT_INDENT}${line}`)
				.join('\n');
		}
		case 'system': {
			// The shout is the signature; CSS is just where it lives on screen.
			return spokenText.toUpperCase();
		}
		default: {
			// environment as authored; a speaker as authored, under its name
			// when the label rule says so; player echoes never reach here (the
			// echo is its group's prompt line).
			const label = labelsBySpeaker.get(entry.source);

			return label === undefined ? spokenText : `${label}: ${spokenText}`;
		}
	}
};

/**
 * A which-one ask's line with its options named exactly once: as the line
 * already says them (D229), or composed from what it offered (D86).
 *
 * @param bundle - The loaded bundle (which names refuse the article)
 * @param entry - The committed entry
 * @returns The text to print
 */
const namingItsOptions = (bundle: EngineGameBundle, entry: RuntimeTranscriptEntry): string => {
	if (entry.offeredAnswers === undefined) {
		return entry.text;
	}

	const options = offeredAnswersSentence(bundle, entry.offeredAnswers);

	return entry.text.includes(options) ? entry.text : `${entry.text} ${options}`;
};
